/* eslint-disable no-return-assign */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-param-reassign */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import uuid from 'react-uuid';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { CustomCompactContainer, LedgerTable } from 'components';
import FeaturedSpreadsheetContext from 'components/FeaturedSpreadsheet/context/FeaturedSpreadsheetContext';
import { cellsParser } from 'components/LedgerTable/utilities/ledgerParser';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { CashDistributionLedgerAcquisition } from 'pages/CapTable/fund-ownership/components/cash-distribution/CashDistributionLedger.props';
import {
  AMOUNT_ALIAS,
  COST_BASIS_ALIAS,
  PURCHASE_DATE_ALIAS,
} from 'pages/CapTable/fund-ownership/components/shares-ledger/constants';
import useExchangeRateMap from 'pages/CapTable/fund-ownership/components/shares-ledger/hooks/useExchangeRateMap';
import { applyPreFxAnnotation } from 'pages/CapTable/fund-ownership/components/shares-ledger/utilities/applyPreFxAnnotation';
import { toString } from 'utilities';
import { colConfig } from './colConfig';
import { PurchasedTableProps } from './PurchasedTable.props';
import conditions from '../../conditions';

function updateInvestmentDates(
  investmentDates: string[],
  parsedCells: { [key: string]: ICell },
  [key, cell]: [string, ICell]
) {
  if (cell.alias === PURCHASE_DATE_ALIAS) {
    const isListed = investmentDates.find(date => toString(date) === toString(cell.value as number));
    return {
      ...parsedCells[key],
      value: isListed ? cell.value : '',
    };
  }
  return cell;
}

const PurchasedTable = ({
  cell,
  setIsAlertVisible,
  doFullValidation,
  setIsValid,
  updateValidationStatus,
  investmentDates,
  isDisabled,
  modeledAfterConvertibleNote,
  fundCurrency,
  isDifferentCurrency,
  selectedMeasurementDate,
  format,
  acquisitionsData,
  setAcquisitionsData,
  purchasedCells,
  setPurchasedCells,
  setDeletedAcquisitions,
  reverseParsedAcquisitions,
  isWarrantOrOption,
  sharesMaxDecimalPlaces,
}: PurchasedTableProps) => {
  const emptyRow = modeledAfterConvertibleNote
    ? { purchase_date: null, amount: null, acquisition_ref: uuid(), sales: [], conversions: [] }
    : { purchase_date: null, shares: null, cost_basis: null, acquisition_ref: uuid(), sales: [] };

  const { cells } = useContext(FeaturedSpreadsheetContext);
  const [rows, setRows] = useState<CashDistributionLedgerAcquisition[]>([]);

  // FOD parents inherit the company's captable_currency as the default Pre-FX
  // source currency. Pulled from the global store rather than threaded
  // through every shares-ledger prop chain — the company is fixed for the
  // dialog's lifetime so a useStore read here is cheap.
  const [{ companyInfo }] = useStore() as unknown as UseStoreValues;
  const defaultPreFxCurrency = companyInfo?.captable_currency as string | undefined;

  // Live mirror of the parsed cell map. The Pre-FX preview popout reads
  // each row's date from this ref at request time, so a freshly-added
  // acquisition (whose `purchase_date` isn't in `acquisitionsData` until
  // reverse-parse) still surfaces the user's typed date.
  const purchasedCellsRef = useRef(purchasedCells);
  useEffect(() => {
    purchasedCellsRef.current = purchasedCells;
  }, [purchasedCells]);

  const dataSourceKey = useMemo(() => cell.linkedCells?.values().next().value?.key, [cell?.columnLegend]);

  const { exchangeRateMap, linkedCellChanges } = useExchangeRateMap({
    fundCurrency,
    format,
    shares: rows,
    selectedMeasurementDate,
    isDifferentCurrency,
    cellKeys: modeledAfterConvertibleNote ? [AMOUNT_ALIAS] : [COST_BASIS_ALIAS],
    acquisitionsData: reverseParsedAcquisitions,
    setAcquisitionsData,
    dateProp: PURCHASE_DATE_ALIAS,
  });

  const addRow = useCallback(() => {
    const latestAcquisitions = reverseParsedAcquisitions || [];
    setAcquisitionsData(prevState => ({
      ...prevState,
      acquisitions: [...latestAcquisitions, emptyRow] as CashDistributionLedgerAcquisition[],
    }));
  }, [emptyRow, setAcquisitionsData, reverseParsedAcquisitions]);

  const deleteRow = useCallback(
    rowIndex => {
      const tmpData = [...reverseParsedAcquisitions];
      const acquisitionToDelete = tmpData[rowIndex];
      const remaining = tmpData.filter(acq => acq.acquisition_ref !== acquisitionToDelete.acquisition_ref);
      if (acquisitionToDelete.id) {
        setDeletedAcquisitions(prevState => [...prevState, acquisitionToDelete.id!]);
      }
      setAcquisitionsData({ ...tmpData, acquisitions: remaining });
    },
    [reverseParsedAcquisitions, setAcquisitionsData, setDeletedAcquisitions]
  );

  const config = useMemo(
    () => colConfig(investmentDates, modeledAfterConvertibleNote, cell.isCustomSecurity, isWarrantOrOption, sharesMaxDecimalPlaces),
    [investmentDates, modeledAfterConvertibleNote, cell.isCustomSecurity, isWarrantOrOption, sharesMaxDecimalPlaces]
  );

  // Set Rows
  useEffect(() => {
    if (cells && dataSourceKey && cells[dataSourceKey] && acquisitionsData?.acquisitions) {
      if (isEmpty(acquisitionsData?.acquisitions)) {
        setRows([]);
      } else {
        /* cells[shares_distribution_ledger_A] doesn't have cost_basis
        if coming from convertible notes */
        const fromExistingOrConvertibleNotesPurchased = acquisitionsData.acquisitions.map(row => ({
          ...row,
          cost_basis: row.cost_basis ?? row.amount ?? null,
          shares: row.shares ?? null,
        }));
        setRows(fromExistingOrConvertibleNotesPurchased || []);
      }
    }
  }, [cells, dataSourceKey, acquisitionsData]);

  // Parse cells
  useEffect(() => {
    if (Array.isArray(rows)) {
      let parsedCells = cellsParser(rows, config)

      // Attach Pre-FX annotation viewer to the host money cell on every
      // acquisition row, persisted or fresh. Convertible-note rows host on
      // AMOUNT; regular acquisitions host on COST_BASIS. The backend's
      // SharesAcquisition writable accepts both aliases and routes the
      // persisted annotation onto whichever column was populated.
      //
      // `parentRef` is the row's stable `acquisition_ref` UUID — used when
      // `acquisition.id` is absent (newly-added rows). The save flow
      // resolves the synthetic key back to the new server-side row by ref.
      const hostMoneyAlias = modeledAfterConvertibleNote ? AMOUNT_ALIAS : COST_BASIS_ALIAS;
      parsedCells = Object.entries(parsedCells).reduce((acc, [key, parsedCell]) => {
        const cellTyped = parsedCell as ICell;
        if (cellTyped.alias === hostMoneyAlias) {
          const acquisition = rows[cellTyped.rowNumber!];
          // Read the date dynamically from the live cell map so the preview
          // sees what the user actually typed — `acquisition.purchase_date`
          // lags until reverse-parse, which means fresh rows would never
          // resolve a date through the static prop. The popout normalizes
          // MM/DD/YYYY -> ISO and skips when empty.
          const rowNumber = cellTyped.rowNumber!;
          const getPurchaseDate = (): string | undefined | null => {
            const dateCell = purchasedCellsRef.current?.[`${PURCHASE_DATE_ALIAS}${rowNumber + 1}`] as
              | { value?: string | null }
              | undefined;
            const fromCells = (dateCell?.value as string | null | undefined) ?? undefined;
            // Fall back to `acquisition.purchase_date` for persisted rows
            // whose cell hasn't been parsed yet (e.g. before the first
            // reverse-parse cycle completes).
            return fromCells ?? (acquisition?.purchase_date as string | null | undefined) ?? undefined;
          };
          // Patch the host cost_basis cell DIRECTLY in `purchasedCells`
          // rather than going through `setAcquisitionsData`. Reasons:
          //   1. `acquisitionsData.shares` (and every other field) lags
          //      until reverse-parse syncs; rebuilding rows from it would
          //      wipe the user's live `shares` / `date` edits the moment
          //      the preview lands.
          //   2. `purchasedCells` is the authoritative source for the
          //      LedgerTable's rendering AND for `reverseParsedAcquisitions`
          //      (which feeds save), so the update flows through to save
          //      without round-tripping.
          const hostCellKey = cellTyped.key;
          const onHostPreview = hostCellKey
            ? (value: number | null) => {
              setPurchasedCells(prev => ({
                ...prev,
                [hostCellKey]: {
                  ...prev[hostCellKey],
                  value,
                  expr: value === null ? '' : String(value),
                },
              }));
            } : undefined;
          return {
            ...acc,
            [key]: applyPreFxAnnotation({
              cell: cellTyped,
              parentType: 'sharesacquisition',
              parentId: acquisition?.id,
              parentRef: acquisition?.acquisition_ref ?? null,
              fieldAlias: hostMoneyAlias,
              defaultCurrency: defaultPreFxCurrency,
              hostCurrency: defaultPreFxCurrency,
              transactionDate: getPurchaseDate,
              onHostPreview,
            }),
          };
        }
        return { ...acc, [key]: cellTyped };
      }, {});

      if (investmentDates && !Array.isArray(investmentDates)) {
        parsedCells = {
          ...parsedCells,
          date1: {
            ...parsedCells.date1,
            value: investmentDates,
            readOnly: true,
          },
        };
      } else if (Array.isArray(investmentDates) && investmentDates.length) {
        parsedCells = Object.entries(parsedCells).reduce((acc, [key, parsedCell]) => {
          const updatedCell = updateInvestmentDates(investmentDates, parsedCells, [key, parsedCell as ICell]);

          return { ...acc, [key]: updatedCell };
        }, {});
      }

      setPurchasedCells(parsedCells);
    }
  }, [rows, investmentDates, isDifferentCurrency, modeledAfterConvertibleNote, defaultPreFxCurrency]);

  return (
    <CustomCompactContainer direction="column" rowSpacing={2.5} wrap="nowrap">
      <Grid>
        <h4>Purchased</h4>
      </Grid>

      <Grid>
        <LedgerTable
          colConfig={config}
          rows={rows}
          tableTerms={{
            tableName: 'Purchased Shares',
            tableSlug: 'purchased-shares',
            columnName: `${modeledAfterConvertibleNote ? 'Note' : 'Shares'}`,
            pluralColumnName: 'Purchased Shares',
          }}
          setIsAlertVisible={setIsAlertVisible}
          disabled={isDisabled}
          addRow={addRow}
          deleteRow={deleteRow}
          cells={purchasedCells}
          setCells={setPurchasedCells}
          tableData={{ acquisitions: reverseParsedAcquisitions }}
          doFullValidation={doFullValidation}
          setIsValid={setIsValid}
          updateValidationStatus={updateValidationStatus}
          conditions={conditions}
          currency={format.currency}
          linkedCellUpdates={linkedCellChanges}
          exchangeRateMap={exchangeRateMap}
        />
      </Grid>
    </CustomCompactContainer>
  );
};

export default PurchasedTable;
